import { useMemo } from "react";

import { translate } from "app/filters/i18nFilter";
import type { TabbedSelectComboboxTab } from "app/pages/sales-intelligence/common-components/dropdown/TabbedSelectCombobox";
import { TabbedSelectCombobox } from "app/pages/sales-intelligence/common-components/dropdown/TabbedSelectCombobox";
import { FormFieldLabel } from "app/pages/sales-intelligence/common-components/form-fields";
import { FilterContentContainer } from "app/pages/sales-intelligence/modules/advanced-search-filtering/components/styles";
import type { IFilterViewBaseModelProps } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterInstance";
import useDropdownTabbedSearch from "app/pages/sales-intelligence/modules/technology/hook/useDropdownTabbedSearch";

import { SELECTED_ALL_PREFIX } from "../../../constants";
import useDropdownWithAllSelectedItems from "../../../hooks/useDropdownWithAllSelectedItems";
import useManageAllCategories from "../../../hooks/useManageAllCategories";
import type { IAppsAndroidCategoryFilter } from "../../../types/androidFiltersDto";
import type { IAppsAndroidCategoryDescriptionDto, IAppsCategoryItemDto } from "../../../types/descriptionDto";

const AppAndroidCategoryFilter = ({
    filter,
    value,
    onFilterUpdate,
}: IFilterViewBaseModelProps<IAppsAndroidCategoryFilter, IAppsAndroidCategoryDescriptionDto>) => {
    const { selectedAllCategory, setSelectedAllCategory } = useManageAllCategories<
        IAppsAndroidCategoryFilter,
        IAppsAndroidCategoryDescriptionDto
    >({
        options: filter.options,
        values: value as IAppsAndroidCategoryFilter,
    });

    const handleOnItemClick = (item: { name: string }) => {
        const categoryValues = (value as IAppsAndroidCategoryFilter)?.values || [];

        const isAllCategory = selectedAllCategory.hasOwnProperty(item.name);

        if (isAllCategory) {
            const isSelected = !selectedAllCategory[item.name];

            setSelectedAllCategory({
                ...selectedAllCategory,
                [item.name]: isSelected,
            });

            const categoryItems = filter.options.categories.find((category) =>
                item.name.includes(category.name.trim().toLowerCase()),
            );

            let filterUpdatesParams: IAppsAndroidCategoryFilter | undefined = undefined;
            if (isSelected && categoryItems) {
                filterUpdatesParams = {
                    values: Array.from(new Set([...categoryValues, ...categoryItems.subCategories])),
                };
            } else {
                const filteredCategoryValues: string[] = categoryValues.filter(
                    (item: string) => !categoryItems?.subCategories.includes(item),
                );
                if (filteredCategoryValues.length > 0) {
                    filterUpdatesParams = {
                        values: filteredCategoryValues,
                    };
                }
            }

            onFilterUpdate(filterUpdatesParams);
        } else {
            const category = filter.options.categories.find((categoryItem) =>
                categoryItem.subCategories.includes(item.name),
            );

            if (category) {
                const selectedAllCategoryName = `${SELECTED_ALL_PREFIX} ${category.name.trim().toLowerCase()}`;

                if (categoryValues.includes(item.name)) {
                    setSelectedAllCategory({
                        ...selectedAllCategory,
                        [selectedAllCategoryName]: false,
                    });
                } else {
                    const isFullCategory = category.subCategories.every((categoryItem: string) =>
                        [...categoryValues, item.name].includes(categoryItem),
                    );

                    if (isFullCategory) {
                        setSelectedAllCategory({
                            ...selectedAllCategory,
                            [selectedAllCategoryName]: true,
                        });
                    }
                }
            }

            let filterUpdatesParams: IAppsAndroidCategoryFilter | undefined = undefined;
            if (!categoryValues.includes(item.name)) {
                filterUpdatesParams = {
                    values: [...categoryValues, item.name],
                };
            } else {
                const filteredCategoryValues: string[] = categoryValues.filter(
                    (category: string) => category !== item.name,
                );

                if (filteredCategoryValues.length > 0) {
                    filterUpdatesParams = {
                        values: filteredCategoryValues,
                    };
                }
            }

            onFilterUpdate(filterUpdatesParams);
        }
    };

    const tabs = useMemo(() => {
        const categoryValues = (value as IAppsAndroidCategoryFilter)?.values || [];
        return filter.options.categories.map((option: IAppsCategoryItemDto, i: number) => {
            return {
                id: i.toString(),
                name: option.name,
                items: option.subCategories.map((item: string) => ({
                    name: item,
                    isSelected: categoryValues.includes(item),
                })),
            };
        });
    }, [filter.options, value]);

    const { tabsWithSortedItems, searchTerm, searchProps } = useDropdownTabbedSearch({
        tabs,
        searchPlaceholder: translate("si.find_apps.filters.android-category.dropdown.placeholder"),
    });

    const { tabsWithAllSelectedItems } = useDropdownWithAllSelectedItems({
        tabs: tabsWithSortedItems,
        selectedAllCategory,
    });

    const dropdownTitle = translate("si.find_apps.filters.android-category.dropdown.title");

    return (
        <FilterContentContainer>
            <FormFieldLabel text={translate("si.find_apps.filters.android-category.label")}>
                <TabbedSelectCombobox
                    tabs={tabsWithAllSelectedItems as readonly TabbedSelectComboboxTab[]}
                    onItemClick={handleOnItemClick}
                    searchTerm={searchTerm}
                    onSearchChange={searchProps.onChange}
                    searchPlaceholder={searchProps.placeholder}
                    buttonText={dropdownTitle}
                    dataAutomation="app-android-category-filter"
                />
            </FormFieldLabel>
        </FilterContentContainer>
    );
};

export default AppAndroidCategoryFilter;
